import {
  FREESTAR_INCONTENT_MAX,
  FREESTAR_INFEED_MAX,
  FREESTAR_NATIVE_MAX,
  freestarPlacementName,
  type FreestarPlacement,
} from './placements';

export type FreestarPlacementAllocation = {
  placement: FreestarPlacement;
  placementName: string;
  slotId: string;
};

type PlacementCounters = {
  belowcontent: number;
  incontent: number;
  infeed: number;
  native: number;
  path: string;
  sidebar_1: number;
  sidebar_2: number;
  top_header: number;
};

const counters: PlacementCounters = {
  belowcontent: 0,
  incontent: 0,
  infeed: 0,
  native: 0,
  path: '',
  sidebar_1: 0,
  sidebar_2: 0,
  top_header: 0,
};

const resetCounters = (path: string) => {
  counters.belowcontent = 0;
  counters.incontent = 0;
  counters.infeed = 0;
  counters.native = 0;
  counters.path = path;
  counters.sidebar_1 = 0;
  counters.sidebar_2 = 0;
  counters.top_header = 0;
};

const ensurePathCounters = () => {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path !== counters.path) {
    resetCounters(path);
  }
};

const nextIndexed = (placement: 'incontent' | 'infeed' | 'native', max: number): FreestarPlacementAllocation | null => {
  if (counters[placement] >= max) return null;
  counters[placement] += 1;
  const placementName = freestarPlacementName(placement, counters[placement]);
  return {
    placement,
    placementName,
    slotId: placementName,
  };
};

const nextReusable = (
  placement: 'sidebar_1' | 'sidebar_2' | 'top_header' | 'belowcontent',
): FreestarPlacementAllocation => {
  counters[placement] += 1;
  const placementName = freestarPlacementName(placement);
  const index = counters[placement];
  // First instance keeps the PubOS name so cls.css / sticky-ad rules apply; later
  // concurrent instances need unique slotIds (Freestar reuse rule).
  const slotId = index === 1 ? placementName : `${placementName}-${index}`;
  return { placement, placementName, slotId };
};

export const allocateFreestarPlacement = (placement: FreestarPlacement): FreestarPlacementAllocation | null => {
  ensurePathCounters();

  switch (placement) {
    case 'top_header':
      return nextReusable('top_header');
    case 'sidebar_1':
      return nextReusable('sidebar_1');
    case 'sidebar_2':
      return nextReusable('sidebar_2');
    case 'belowcontent':
      return nextReusable('belowcontent');
    case 'incontent':
      return nextIndexed('incontent', FREESTAR_INCONTENT_MAX);
    case 'infeed':
      return nextIndexed('infeed', FREESTAR_INFEED_MAX);
    case 'native':
      return nextIndexed('native', FREESTAR_NATIVE_MAX);
    default:
      return null;
  }
};

/** Test helper — resets module counters between specs. */
export const resetFreestarPlacementCountersForTests = (path = ''): void => {
  resetCounters(path);
};
